import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, input, model, output, viewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatToolbar } from '@angular/material/toolbar';

import { BaasI18n } from '@lvflanders/bar';

import { BaasActionButton } from '../baas-action-button/baas-action-button';
import { BaasActionButtonOptions } from '../baas-action-button/baas-action-button.types';
import { BarI18nPipe } from '../i18n/bar-i18n.pipe';

@Component({
    selector: 'baas-search-screen',
    templateUrl: './baas-search-screen.html',
    imports: [NgTemplateOutlet, MatButton, MatToolbar, BaasActionButton, BarI18nPipe]
})
/** BAR search screen placeholder component which defines the search screen layout in a technology agnostic way. */
export class BaasSearchScreen {
    readonly #i18n = inject(BaasI18n);

    readonly searchResults = viewChild.required<ElementRef>('searchResults');
    readonly searchForm = viewChild.required<ElementRef>('searchForm');

    // default no toolbar
    readonly showToolbar = input(false);
    // Dynamic actions in toolbar
    readonly actions = input<BaasActionButtonOptions[]>();
    // Enable extended criteria for the search
    readonly hasExtendedCriteria = input(false);
    // Extra info section above the search results.
    readonly hasInfoSection = input(false);
    // Whether the info section is fixed and should always appear.
    readonly fixedInfoSection = input(true);
    // Disabled search (to be used with validation or...)
    readonly searchDisabled = input(false);
    // If the form was submitted and a search was triggered
    readonly searched = model(false);
    // Optional: Extended search fields
    readonly showExtendedCriteria = model(false);
    // Optional: use internal form of this component
    // Provide a formGroup with the searchCriteria / extendedSearchCriteria
    readonly internalForm = input(false);
    // Manual search means that we tell the component when the search is done through the searched property
    readonly manualSearch = input(false);
    // Scroll into view on search/init
    readonly autoScroll = input(true);
    /** Use this property to override the default search criteria header */
    readonly headerSearchCriteria = input<string>();
    /** Use this property to override the default search results header */
    readonly headerSearchResults = input<string>();
    /** Allows to override the text on the search button */
    readonly searchText = input<string>(this.#i18n.get('DOMAIN_BAR_CONTROLS.SEARCH_SCREEN.SEARCH'));
    /** Allows to override the text on the reset search button */
    readonly resetText = input<string>(this.#i18n.get('DOMAIN_BAR_CONTROLS.SEARCH_SCREEN.SEARCH_CRITERIA_RESET'));
    /** Whether pressing the enter key on a global level triggers the search. By global we mean if the search filters are not focussed. */
    readonly globalEnterTriggersSearch = input(false);
    // User clicked search
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly search = output();
    // User clicked initialize form
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly reset = output();

    protected readonly extendedCriteriaToggleText = computed(() => {
        return this.#i18n.get(`DOMAIN_BAR_CONTROLS.SEARCH_SCREEN.SEARCH_FIELDS_${this.showExtendedCriteria() ? 'HIDE' : 'SHOW'}`);
    });

    @HostListener('window:keydown.enter', ['$event'])
    onKeyDown(e: Event) {
        if (this.globalEnterTriggersSearch()) {
            e.preventDefault();
            this.doSearch();
        }
    }

    doSearch(): void {
        if (!this.manualSearch()) this.searched.set(true);
        this.search.emit();
        if (this.autoScroll()) {
            this.searchResults().nativeElement.scrollIntoView(true);
        }
    }

    doReset(): void {
        this.reset.emit();
        if (this.autoScroll()) {
            this.searchForm().nativeElement.scrollIntoView(true);
        }
    }

    toggleExtendedCriteria() {
        this.showExtendedCriteria.update((showExtendedCriteria) => !showExtendedCriteria);
    }
}
